import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiGet } from '../api/client.js';
import PipelinePanel from '../components/PipelinePanel.jsx';
import Sparkline from '../components/Sparkline.jsx';
import { PostActions } from '../components/PostActions.jsx';
import { PostFilters, buildPostsQuery } from '../components/PostFilters.jsx';
import { useProjectScope } from '../lib/projectScope.js';
import { STATE_LABEL, PILLAR_LABEL, fmtAgo } from '../lib/labels.js';

export default function Dashboard() {
  const projectsQ = useQuery({
    queryKey: ['projects'],
    queryFn: ({ signal }) => apiGet('/projects', { signal }),
  });

  const [scope] = useProjectScope();
  const [filters, setFilters] = useState({ state: '', pillar: '', from: '', to: '' });
  const postsQs = buildPostsQuery({ ...filters, project: scope, limit: 30 });
  const postsQ = useQuery({
    queryKey: ['posts', { ...filters, project: scope, limit: 30 }],
    queryFn: ({ signal }) => apiGet(`/posts?${postsQs}`, { signal }),
  });

  // Engagement window: rolling last-N-days chips OR an explicit from/to
  // range (owner request 2026-07-26). from/to wins when either is set.
  const [range, setRange] = useState({ days: 30, from: '', to: '' });
  const rangeQs = (range.from || range.to)
    ? `${range.from ? `&from=${range.from}` : ''}${range.to ? `&to=${range.to}` : ''}`
    : `&days=${range.days}`;

  const engagementQ = useQuery({
    queryKey: ['engagement-summary', { ...range, project: scope }],
    queryFn: ({ signal }) => apiGet(`/engagement/summary?_=1${rangeQs}${scope ? `&project=${encodeURIComponent(scope)}` : ''}`, { signal }),
  });

  const trendQ = useQuery({
    queryKey: ['engagement-timeseries', { ...range, metric: 'impressions' }],
    queryFn: ({ signal }) => apiGet(`/engagement/timeseries?metric=impressions${rangeQs}`, { signal }),
    // Only chart trend once we know there's at least one publication.
    enabled: Boolean(engagementQ.data?.totals?.publication_count),
  });

  const scopedProjects = scope
    ? (projectsQ.data?.projects || []).filter((p) => p.key === scope)
    : (projectsQ.data?.projects || []);

  return (
    <div className="dashboard">
      <ActionNeeded scope={scope} />

      <EngagementSummary q={engagementQ} trendQ={trendQ} range={range} onRange={setRange} />

      <section className="section">
        <h1>{scope ? 'Project' : 'Projects'}</h1>
        {projectsQ.isLoading && <div className="loading">Loading projects…</div>}
        {projectsQ.isError && (
          <div className="error">Couldn’t load projects: {projectsQ.error.message}</div>
        )}
        {projectsQ.data && (
          <div className="project-grid">
            {scopedProjects.length === 0 && (
              <div className="empty">No projects yet.</div>
            )}
            {scopedProjects.map((p) => (
              <Link key={p.id} to={`/projects/${p.key}`} className="project-card">
                <div className="project-card-head">
                  <h3>{p.display_name}</h3>
                  {p.ig_handle && <span className="ig-handle">{p.ig_handle}</span>}
                </div>
                <p className="project-positioning">{p.positioning}</p>
                <div className="project-stats">
                  <span><b>{p.active_pillar_count}</b> pillars</span>
                  <span><b>{p.post_count}</b> posts</span>
                  <span><b>{p.social_account_count}</b> social</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="section">
        <h1>Recent posts</h1>
        <PostFilters {...filters} onChange={setFilters} />
        {postsQ.isLoading && <div className="loading">Loading posts…</div>}
        {postsQ.isError && (
          <div className="error">Couldn’t load posts: {postsQ.error.message}</div>
        )}
        {postsQ.data && (
          <>
            {postsQ.data.posts.length === 0 && (
              <div className="empty">No posts match this filter.</div>
            )}
            {postsQ.data.posts.length > 0 && (
              <div className="post-table">
                <div className="post-row post-row-head post-row-dash-act">
                  <span>Project</span>
                  <span>Pillar</span>
                  <span>Title</span>
                  <span>State</span>
                  <span>When</span>
                  <span>Actions</span>
                </div>
                {postsQ.data.posts.map((p) => (
                  <Link key={p.id} to={`/posts/${p.id}`} className="post-row post-row-dash-act">
                    <span className="project-tag">{p.project_name}</span>
                    <span className="pillar-tag">{PILLAR_LABEL[p.pillar_key] || p.pillar_key}</span>
                    <span className="post-title">{p.title || <em>(no title)</em>}</span>
                    <span className={`state state-${p.state}`}>{STATE_LABEL[p.state] || p.state}</span>
                    <span className="when" title={p.created_at}>{fmtAgo(p.created_at)}</span>
                    <PostActions post={p} />
                  </Link>
                ))}
              </div>
            )}
            <div className="pagination">
              Showing {postsQ.data.posts.length} of {postsQ.data.pagination.total} total
            </div>
          </>
        )}
      </section>

      <details className="how-it-works">
        <summary>How Markaii works — the 8-stage Loop</summary>
        <PipelinePanel />
      </details>
    </div>
  );
}

/**
 * ActionNeeded — operator-first dashboard header (ADR-0029). Surfaces the
 * things that need a human: posts awaiting approval and failed posts. Counts
 * come from /posts pagination totals (cheap, limit=1), scoped to the selected
 * project. Hidden entirely when there's nothing to act on.
 */
function ActionNeeded({ scope }) {
  const scopeQ = scope ? `&project=${encodeURIComponent(scope)}` : '';
  const reviewQ = useQuery({
    queryKey: ['posts-count', 'review', scope],
    queryFn: ({ signal }) => apiGet(`/posts?state=review&limit=1${scopeQ}`, { signal }),
  });
  const failedQ = useQuery({
    queryKey: ['posts-count', 'failed', scope],
    queryFn: ({ signal }) => apiGet(`/posts?state=failed&limit=1${scopeQ}`, { signal }),
  });

  const pending = reviewQ.data?.pagination?.total ?? 0;
  const failed = failedQ.data?.pagination?.total ?? 0;
  const loading = reviewQ.isLoading || failedQ.isLoading;
  const scopeSuffix = scope ? `&project=${encodeURIComponent(scope)}` : '';

  return (
    <section className="action-needed">
      <h1>{loading ? 'Action needed' : (pending + failed === 0 ? 'All caught up' : 'Action needed')}</h1>
      <div className="action-cards">
        <Link to="/inbox" className={`action-card ${pending > 0 ? 'attn' : 'ok'}`}>
          <span className="action-num">{pending}</span>
          <span className="action-lbl">pending approval</span>
        </Link>
        <Link to={`/posts?state=failed${scopeSuffix}`} className={`action-card ${failed > 0 ? 'danger' : 'ok'}`}>
          <span className="action-num">{failed}</span>
          <span className="action-lbl">failed {failed === 1 ? 'post' : 'posts'}</span>
        </Link>
      </div>
    </section>
  );
}

function EngagementSummary({ q, trendQ, range, onRange }) {
  // Group trend points by platform once, even when trendQ hasn't resolved yet.
  // Hooks must run unconditionally — early returns below still respect that
  // because useMemo always sees the same input shape via optional chaining.
  const seriesByPlatform = useMemo(() => {
    const points = trendQ?.data?.points || [];
    const grouped = {};
    for (const pt of points) {
      if (!grouped[pt.platform]) grouped[pt.platform] = [];
      grouped[pt.platform].push(pt.value);
    }
    return grouped;
  }, [trendQ?.data?.points]);

  if (q.isLoading || q.isError) return null;
  const data = q.data;
  const totals = data?.totals;
  if (!totals || Number(totals.publication_count) === 0) {
    // Hide entirely until at least one publication has been published in the
    // window — keeps the dash clean for brand-new projects.
    return null;
  }
  const fmt = (n) => Number(n).toLocaleString();
  // Honest signal: publications exist but no engagement has been ingested.
  // Almost always a platform-side block — Instagram insights permission
  // (Meta App Review) or a disconnected/expired social account — not "0 engagement".
  const noStats = ['total_impressions', 'total_reach', 'total_likes', 'total_saves', 'total_shares', 'total_comments']
    .every((k) => Number(totals[k]) === 0);
  return (
    <section className="section">
      <div className="section-h-row" style={{ alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>
          Engagement — {(range.from || range.to)
            ? `${range.from || 'start'} → ${range.to || 'today'}`
            : `last ${data.window_days} days`}
        </h1>
        <div className="filter-row" style={{ margin: 0 }}>
          {[7, 30, 90].map((d) => (
            <button key={d} type="button"
              className={`filter-chip ${!range.from && !range.to && range.days === d ? 'active' : ''}`}
              onClick={() => onRange({ days: d, from: '', to: '' })}>
              {d}d
            </button>
          ))}
          <label className="filter-date">
            <input type="date" value={range.from} max={range.to || undefined}
              onChange={(e) => onRange({ ...range, from: e.target.value })} />
          </label>
          <label className="filter-date">
            <input type="date" value={range.to} min={range.from || undefined}
              onChange={(e) => onRange({ ...range, to: e.target.value })} />
          </label>
          {(range.from || range.to) && (
            <button type="button" className="btn-compact" onClick={() => onRange({ days: 30, from: '', to: '' })}>
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="engagement-summary">
        <SummaryTile label="Publications" value={fmt(totals.publication_count)} />
        <SummaryTile label="Impressions" value={fmt(totals.total_impressions)} />
        <SummaryTile label="Reach" value={fmt(totals.total_reach)} />
        <SummaryTile label="Likes" value={fmt(totals.total_likes)} />
        <SummaryTile label="Saves" value={fmt(totals.total_saves)} />
        <SummaryTile label="Shares" value={fmt(totals.total_shares)} />
        <SummaryTile label="Comments" value={fmt(totals.total_comments)} />
      </div>
      {noStats && (
        <p className="engagement-note">
          {fmt(totals.publication_count)} post{Number(totals.publication_count) === 1 ? '' : 's'} published, but no engagement stats have come in yet.
          This is usually a platform-side block — Instagram needs the insights permission (Meta App Review), or a social account is
          disconnected or its token expired. Check each project’s <b>Connections</b> tab.
        </p>
      )}
      {data.by_platform?.length > 0 && (
        <div className="engagement-table-wrap">
          <table className="engagement-table">
            <thead>
              <tr>
                <th>Platform</th><th>Posts</th><th>Impressions</th><th>Reach</th>
                <th>Likes</th><th>Saves</th><th>Comments</th><th>Trend</th>
              </tr>
            </thead>
            <tbody>
              {data.by_platform.map((p) => {
                const series = seriesByPlatform[p.platform] || [];
                const na = PLATFORM_METRIC_NOTES[p.platform] || {};
                const cell = (key, value) => (na[key]
                  ? <span className="metric-na" title={na[key]}>n/a *</span>
                  : fmt(value));
                return (
                  <tr key={p.platform}>
                    <td><span className={`platform-chip platform-${p.platform}`}>{p.platform}</span></td>
                    <td>{fmt(p.publication_count)}</td>
                    <td>{cell('impressions', p.total_impressions)}</td>
                    <td>{cell('reach', p.total_reach)}</td>
                    <td>{cell('likes', p.total_likes)}</td>
                    <td>{cell('saves', p.total_saves)}</td>
                    <td>{cell('comments', p.total_comments)}</td>
                    <td>
                      <Sparkline values={series} width={96} height={22} ariaLabel={`${p.platform} impressions trend`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="muted small" style={{ margin: '6px 2px 0' }}>
            * Not available from the platform: Facebook stopped providing post-level impressions/reach in its API;
            TikTok metrics arrive once the app leaves sandbox (content audit).
          </p>
        </div>
      )}
    </section>
  );
}

// What each platform's API simply does not provide — rendered as "n/a"
// instead of a misleading 0 (owner request 2026-07-26).
// MUST MATCH workers/src/lib/metricAvailability.js (the superset truth —
// it also lists 'shares' cells, which this table simply has no column
// for). Update both together when a platform gains/loses a metric.
const PLATFORM_METRIC_NOTES = {
  facebook: {
    impressions: 'Facebook removed post-level impressions from its API — not available to any tool.',
    reach: 'Facebook removed post-level reach from its API — not available to any tool.',
    saves: 'Facebook does not expose saves for Page posts.',
  },
  tiktok: {
    impressions: 'TikTok metrics become available once the app completes the content audit (posts are private sandbox until then).',
    reach: 'Available after the TikTok content audit.',
    likes: 'Available after the TikTok content audit.',
    saves: 'Available after the TikTok content audit.',
    comments: 'Available after the TikTok content audit.',
  },
  linkedin: {
    impressions: 'LinkedIn post impressions require an analytics API tier the app does not have yet.',
    reach: 'LinkedIn does not expose post-level reach.',
    saves: 'LinkedIn has no saves metric.',
  },
  x: {
    reach: 'X does not expose post-level reach.',
    saves: 'X does not expose bookmark counts via its public metrics.',
  },
};

function SummaryTile({ label, value }) {
  return (
    <div className="summary-tile">
      <div className="summary-tile-label">{label}</div>
      <div className="summary-tile-value">{value}</div>
    </div>
  );
}
